/**
 * Page View Tracking Hook
 *
 * Automatically tracks page views and time spent on each page.
 * Uses the UX analytics API to record navigation patterns.
 */

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { getSessionId, canTrack } from "./session";

/**
 * Map of route paths to human-readable page names
 */
const PAGE_NAMES: Record<string, string> = {
  "/dashboard": "Dashboard",
  "/domains": "Domains",
  "/sources": "Sending Sources",
  "/alerts": "Alerts",
  "/reports": "Reports",
  "/enforcement": "Enforcement",
  "/remediation": "Remediation",
  "/bimi": "BIMI",
  "/help": "Help",
  "/settings": "Settings",
  "/msp": "MSP Portal",
  "/msp/clients": "MSP Clients",
  "/msp/billing": "MSP Billing",
  "/msp/branding": "MSP Branding",
  "/msp/team": "MSP Team",
  "/msp/white-label": "White Label",
  "/audit": "Audit Log",
  "/onboarding": "Onboarding",
  "/login": "Login",
  "/get-started": "Get Started",
  "/checkout": "Checkout",
};

/**
 * Get a human-readable page name from a path
 */
function getPageName(path: string): string {
  // Check for exact match first
  if (PAGE_NAMES[path]) {
    return PAGE_NAMES[path];
  }

  // Check for dynamic routes
  if (path.startsWith("/domains/")) {
    return "Domain Detail";
  }
  if (path.startsWith("/msp/clients/")) {
    return "Client Detail";
  }
  if (path.startsWith("/share/")) {
    return "Shared Dashboard";
  }

  // Fallback: capitalize first segment
  const segment = path.split("/")[1];
  if (segment) {
    return segment.charAt(0).toUpperCase() + segment.slice(1);
  }

  return "Unknown";
}

/**
 * Hook to track page views automatically
 *
 * Usage:
 * ```tsx
 * function App() {
 *   usePageTracking();
 *   return <Routes>...</Routes>;
 * }
 * ```
 */
export function usePageTracking(): void {
  const location = useLocation();
  const pageViewId = useRef<string | null>(null);
  const startTime = useRef<number>(Date.now());
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    if (!canTrack()) return;

    // Don't track the same page twice (can happen with query string changes)
    if (lastPath.current === location.pathname) {
      return;
    }

    const previousPath = lastPath.current;
    lastPath.current = location.pathname;

    // Track time on previous page
    if (pageViewId.current && previousPath) {
      const timeOnPage = Date.now() - startTime.current;

      // Use sendBeacon for reliability (fire-and-forget)
      try {
        const beaconUrl = `${import.meta.env.VITE_API_URL || ""}/api/analytics/ux/page-view/${pageViewId.current}/time`;
        navigator.sendBeacon(
          beaconUrl,
          JSON.stringify({ timeOnPageMs: timeOnPage })
        );
      } catch {
        // Fallback to regular fetch
        api
          .updatePageViewTime(pageViewId.current, timeOnPage)
          .catch(() => {});
      }
    }

    // Reset timer for new page
    startTime.current = Date.now();

    // Track new page view
    const pageName = getPageName(location.pathname);

    api
      .trackPageView({
        sessionId: getSessionId(),
        pagePath: location.pathname,
        pageName,
        referrerPath: previousPath,
        viewportWidth: window.innerWidth,
        isMobile: window.innerWidth < 768,
      })
      .then((response) => {
        if (response.id) {
          pageViewId.current = response.id;
        }
      })
      .catch(() => {
        // Silently fail - analytics should never break the app
      });

    // Cleanup: track time when component unmounts or page changes
    return () => {
      if (pageViewId.current) {
        const timeOnPage = Date.now() - startTime.current;
        try {
          const beaconUrl = `${import.meta.env.VITE_API_URL || ""}/api/analytics/ux/page-view/${pageViewId.current}/time`;
          navigator.sendBeacon(
            beaconUrl,
            JSON.stringify({ timeOnPageMs: timeOnPage })
          );
        } catch {
          // Ignore errors on cleanup
        }
      }
    };
  }, [location.pathname]);
}
